import React from 'react';

import type {
  DraftEntity,
  DraftParentInfo,
} from '@/lib/crm/core/draftEntities';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';

import { CrmFieldValueInput } from '../fields/CrmFieldValueInput';

export interface AssociationInputProps {
  associationType: AssociationType;
  value: string | string[] | null | undefined;
  onChange: (value: string | string[] | null) => void;
  orgId: number;
  disabled?: boolean;
  /**
   * @deprecated Use CrmAssociationField instead for labeled inputs with edit indicators.
   * This prop is ignored - AssociationInput now renders label-free inputs.
   */
  isEdited?: boolean;
  /** Source object's entity_id - used to prepopulate reverse association when creating */
  sourceEntityId?: string;
  /** Optional guard callback before opening create modal. Call proceed() to continue. */
  onBeforeCreate?: (proceed: () => void) => void;
  /** Whether a saved value exists (for *_to_one, hides create option if true) */
  hasSavedValue?: boolean;
  /** Set of entity type names that are stage types (have "stage" in entity_kind).
   *  Used to detect pipeline stage associations generically instead of hardcoding names. */
  stageTypeNames?: Set<string>;
  /**
   * Custom create handler — when set, the "Create new" button calls this
   * instead of opening the standard create slideout.
   */
  onCustomCreate?: () => void;
  /**
   * For pipeline-stage associations only: when true, suppresses the
   * mount-time auto-select of the default pipeline's first stage. Use in
   * edit-existing-record flows so opening Edit doesn't spuriously fire an
   * onChange and mark the form as dirty. Ignored for non-stage associations.
   * Forwarded to AssociatedPipelineStageInput.
   */
  disableAutoSelect?: boolean;
  /**
   * When true, conflict-bearing entities are selectable (not disabled) in the
   * picker — conflicts are surfaced via ConflictResolutionPanel instead of
   * blocking selection. Forwarded to AssociatedCrmObjectInput.
   */
  allowOverwrite?: boolean;
  /**
   * Draft-mode create handler — when set, the entity picker's "Create new"
   * flow builds a client-side DraftEntity and reports it here instead of
   * eagerly creating it on the server. Forwarded to CrmFieldValueInput.
   */
  onCreateDraft?: (draft: DraftEntity) => void;
  /** Hide the "Create new …" option entirely (e.g. in filter contexts). Forwarded to CrmFieldValueInput. */
  hideCreateOption?: boolean;
  /**
   * Parent-identity context threaded down from a parent draft form so a
   * nested create can render a "belongs to {parent}" banner. Forwarded to
   * CrmFieldValueInput.
   */
  draftParentInfo?: DraftParentInfo;
}

/**
 * AssociationInput - label-free association editor.
 *
 * Thin adapter over the canonical {@link CrmFieldValueInput} (`kind:
 * 'association'`), which is the single source of truth for routing an
 * association to the grouped pipeline-stage picker or the entity picker.
 * Kept as a named component because many callers (CrmAssociationField, the
 * overview edit rows, etc.) import it directly.
 *
 * Note: renders label-free inputs. For labeled form fields, use
 * CrmAssociationField which wraps this with LabelWithIndicator.
 */
export const AssociationInput: React.FC<AssociationInputProps> = ({
  isEdited: _isEdited, // Deprecated - ignored for backwards compatibility
  value,
  onChange,
  onBeforeCreate,
  hasSavedValue,
  stageTypeNames,
  onCustomCreate,
  disableAutoSelect,
  associationType,
  orgId,
  disabled,
  sourceEntityId,
  allowOverwrite,
  onCreateDraft,
  hideCreateOption,
  draftParentInfo,
}) => {
  return (
    <CrmFieldValueInput
      kind='association'
      associationType={associationType}
      value={value}
      onChange={onChange}
      orgId={orgId}
      stageTypeNames={stageTypeNames}
      disabled={disabled}
      sourceEntityId={sourceEntityId}
      onCustomCreate={onCustomCreate}
      onBeforeCreate={onBeforeCreate}
      allowOverwrite={allowOverwrite}
      hasSavedValue={hasSavedValue}
      disableAutoSelect={disableAutoSelect}
      onCreateDraft={onCreateDraft}
      hideCreateOption={hideCreateOption}
      draftParentInfo={draftParentInfo}
    />
  );
};
